import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Zap, TrendingUp, Users, Headphones, Star, ArrowRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import subscriptionService from '../../services/subscriptionService';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import FeatureShowcaseCard from './components/FeatureShowcaseCard';
import InteractiveDemo from './components/InteractiveDemo';
import PricingComparison from './components/PricingComparison';
import TestimonialCard from './components/TestimonialCard';
import FeatureMatrix from './components/FeatureMatrix';


const PremiumFeaturesShowcase = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [usageData, setUsageData] = useState({});
  const [selectedFeature, setSelectedFeature] = useState('unlimited_tracking');

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    setLoading(true);
    try {
      const { data: subscription } = await subscriptionService?.getCurrentSubscription();
      setCurrentSubscription(subscription);

      // Load usage data to personalize messaging
      const exportUsage = await subscriptionService?.getFeatureUsage('monthly_exports');
      const caregiverUsage = await subscriptionService?.getFeatureUsage('caregiver_connections');
      
      setUsageData({
        monthlyExports: exportUsage,
        caregiverConnections: caregiverUsage
      });
    } catch (error) {
      console.error('Failed to load user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = () => {
    navigate('/subscription-management');
  };

  const isBasicPlan = currentSubscription?.plan?.planType === 'basic';

  const premiumFeatures = [
    {
      id: 'unlimited_tracking',
      icon: 'Zap',
      title: 'Unlimited Tracking',
      description: 'Track symptoms without limits. No monthly caps, no restrictions.',
      benefits: [
        'Unlimited daily check-ins',
        'Unlimited symptom entries',
        'Extended 6-month history',
        'No feature restrictions'
      ],
      demoType: 'tracking',
      color: 'primary'
    },
    {
      id: 'advanced_analytics',
      icon: 'TrendingUp',
      title: 'Advanced Analytics',
      description: 'Powerful insights with pattern recognition and predictive analytics.',
      benefits: [
        'ML-powered pattern detection',
        'Correlation analysis',
        'Predictive health insights',
        'Custom report generation'
      ],
      demoType: 'analytics',
      color: 'success'
    },
    {
      id: 'caregiver_tools',
      icon: 'Users',
      title: 'Caregiver Management',
      description: 'Connect with caregivers and share health data securely.',
      benefits: [
        'Unlimited caregiver connections',
        'Real-time health alerts',
        'Shared family dashboard',
        'Activity timeline tracking'
      ],
      demoType: 'caregiver',
      color: 'warning'
    },
    {
      id: 'priority_support',
      icon: 'Headphones',
      title: 'Priority Support',
      description: 'Get help when you need it with dedicated priority support.',
      benefits: [
        '24/7 priority email support',
        'Live chat assistance',
        'Response within 2 hours',
        'Dedicated account manager'
      ],
      demoType: 'support',
      color: 'accent'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah M.',
      role: 'Premium User',
      rating: 5,
      text: 'The advanced analytics helped me identify patterns I never noticed before. Worth every penny!',
      avatar: 'S'
    },
    {
      name: 'John D.',
      role: 'Family Plan User',
      rating: 5,
      text: 'Being able to monitor my elderly parents health remotely gives me peace of mind.',
      avatar: 'J'
    },
    {
      name: 'Emily R.',
      role: 'Premium User',
      rating: 5,
      text: 'Unlimited exports make sharing data with my doctor so much easier. Highly recommend!',
      avatar: 'E'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading premium features...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-muted-foreground hover:text-foreground transition-smooth mb-2"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </button>
          <h1 className="text-3xl font-heading font-bold text-foreground">Premium Features</h1>
          <p className="text-muted-foreground mt-1">Unlock the full potential of MyBodyToday</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-12">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-primary/10 via-accent/5 to-success/10 rounded-2xl p-8 md:p-12 border border-border shadow-therapeutic-lg">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <Star className="w-4 h-4" />
              Upgrade to Premium Today
            </div>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground mb-4">
              Take Control of Your Health Journey
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Unlock unlimited tracking, advanced analytics, caregiver tools, and priority support. 
              Everything you need to manage your health effectively.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={handleUpgrade} size="lg" className="text-lg px-8">
                Upgrade Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })} variant="outline" size="lg" className="text-lg px-8">
                View Pricing
              </Button>
            </div>
            {isBasicPlan && usageData?.monthlyExports?.usageCount > 0 && (
              <p className="text-sm text-warning mt-4">
                You've used {usageData?.monthlyExports?.usageCount} of {usageData?.monthlyExports?.limit} monthly exports. 
                Upgrade for unlimited access!
              </p>
            )}
          </div>
        </div>

        {/* Feature Showcase Cards */}
        <div className="space-y-6">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-heading font-bold text-foreground mb-2">Premium Features</h2>
            <p className="text-muted-foreground">Explore what you get with Premium access</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {premiumFeatures?.map((feature) => (
              <FeatureShowcaseCard
                key={feature?.id}
                feature={feature}
                isSelected={selectedFeature === feature?.id}
                onClick={() => setSelectedFeature(feature?.id)}
              />
            ))}
          </div>
        </div>

        {/* Interactive Demo Section */}
        <div className="bg-card rounded-2xl border border-border shadow-therapeutic-lg p-6 md:p-8">
          <h2 className="text-2xl font-heading font-bold text-foreground mb-6 text-center">
            See It In Action
          </h2>
          <InteractiveDemo 
            featureType={selectedFeature} 
            usageData={usageData}
          />
        </div>

        {/* Feature Matrix */}
        <div className="space-y-6">
          <div className="text-center">
            <h2 className="text-3xl font-heading font-bold text-foreground mb-2">Complete Feature Comparison</h2>
            <p className="text-muted-foreground">See exactly what you get with each plan</p>
          </div>
          <FeatureMatrix currentPlan={currentSubscription?.plan?.planType} />
        </div>

        {/* Testimonials */}
        <div className="space-y-6">
          <div className="text-center">
            <h2 className="text-3xl font-heading font-bold text-foreground mb-2">What Our Users Say</h2>
            <p className="text-muted-foreground">Join thousands of satisfied Premium users</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials?.map((testimonial, index) => (
              <TestimonialCard key={index} testimonial={testimonial} />
            ))}
          </div>
        </div>

        {/* Pricing Comparison */}
        <div id="pricing" className="space-y-6">
          <div className="text-center">
            <h2 className="text-3xl font-heading font-bold text-foreground mb-2">Choose Your Plan</h2>
            <p className="text-muted-foreground">Flexible pricing that grows with your needs</p>
          </div>
          <PricingComparison 
            currentPlan={currentSubscription?.plan?.planType}
            onUpgrade={handleUpgrade}
          />
        </div>

        {/* Final CTA */}
        <div className="bg-gradient-to-r from-primary to-success rounded-2xl p-8 md:p-12 text-center shadow-therapeutic-xl">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
            Ready to Upgrade?
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Start your Premium journey today and unlock all features. Cancel anytime, no questions asked.
          </p>
          <Button onClick={handleUpgrade} size="lg" variant="secondary" className="text-lg px-8">
            Get Premium Now
            <Zap className="w-5 h-5 ml-2" />
          </Button>
          <p className="text-white/80 text-sm mt-4">
            30-day money-back guarantee • Cancel anytime • Instant activation
          </p>
        </div>
      </div>
    </div>
  );
};

export default PremiumFeaturesShowcase;